import { type ReactNode, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useLocation, useRoute, useSearch } from "wouter";
import {
  Trash2,
  Pencil,
  User2,
  HeartPulse,
  Sparkles,
  UserCheck,
  Printer,
  Share2,
  ClipboardList,
  Clock,
  Utensils,
  GraduationCap,
  Phone,
  PhoneCall,
  type LucideIcon,
} from "lucide-react";
import {
  updateChildSchema,
  type UpdateChildInput,
} from "@shared/validation/child.schema";
import { useChild, useUpdateChild, useDeleteChild } from "../lib/api/useChildren";
import { ChildPhotoUploader } from "../components/child/ChildPhotoUploader";
import { StoredPhotoNotice } from "../components/child/StoredPhotoNotice";
import { LegalNameField } from "../components/child/ChildNameFields";
import {
  LEGAL_NAME_DISPLAY_LABEL,
  NICKNAME_DISPLAY_LABEL,
  NICKNAME_HINT,
  NICKNAME_LABEL,
} from "../components/child/childNameCopy";
import { ConfirmDialog } from "../components/ui/ConfirmDialog";
import { TabNav, TabPanel, type TabItem } from "../components/ui/TabNav";
import { AllergyList } from "../components/child/AllergyList";
import { MedicationList } from "../components/child/MedicationList";
import { MedicalHistorySection } from "../components/child/MedicalHistorySection";
import { AuthorizedPickupsList } from "../components/child/AuthorizedPickupsList";
import { CareAuthorizationSection } from "../components/child/CareAuthorizationSection";
import { ImmunizationsList } from "../components/child/ImmunizationsList";
import { RoutineList } from "../components/child/RoutineList";
import { FeedingSection } from "../components/child/FeedingSection";
import { CarePreferencesSection } from "../components/child/CarePreferencesSection";
import { SharingSection } from "../components/child/SharingSection";
import { IntakeSection } from "../components/child/IntakeSection";
import { useToast } from "../lib/toast/ToastProvider";
import { ApiError } from "../lib/api/client";
import { INPUT_CLASS } from "../lib/forms/inputClass";
import { AppShell } from "../components/AppShell";
import { PlayfulHero } from "../components/PlayfulHero";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { Field } from "../components/ui/Field";
import { SectionCard } from "../components/ui/SectionCard";
import { PageLoader } from "../components/ui/Spinner";
import { SECTION_ACCENT, type AccentName } from "../lib/accents";
import { useCurrentUser } from "../lib/auth/useAuth";
import { useMyOrgs } from "../lib/api/useOrganizations";
import { ORG_SUBJECT_NOUN } from "@shared/constants/orgRoles";
import { PICKUP_CONSENT_ENABLED } from "@shared/constants/features";
import { FeatureSlot } from "../features/_sim/Slot"; // SIM-SEAM: v2 child-profile slot

/** Hero buttons: compact on a phone so all three sit on one row (see the hero). */
const HERO_ACTION = "px-3.5 py-1.5 sm:px-5 sm:py-2";

type TabId =
  | "basic"
  | "medical"
  | "routines"
  | "feeding"
  | "care"
  | "pickup"
  | "sharing"
  | "intake";

// Icons are tinted with each section's SECTION_ACCENT hue so the tab row reads
// as color-coded (the explicit text-* color survives the active tab's white text).
const ALL_TABS: TabItem<TabId>[] = [
  { id: "basic", label: "Basic Info", icon: <User2 className="h-4 w-4 text-sky-600" aria-hidden /> },
  { id: "medical", label: "Medical", icon: <HeartPulse className="h-4 w-4 text-rose-600" aria-hidden /> },
  { id: "routines", label: "Routines", icon: <Clock className="h-4 w-4 text-indigo-600" aria-hidden /> },
  { id: "feeding", label: "Feeding", icon: <Utensils className="h-4 w-4 text-amber-600" aria-hidden /> },
  { id: "care", label: "Care Preferences", icon: <Sparkles className="h-4 w-4 text-emerald-600" aria-hidden /> },
  { id: "pickup", label: "Pickup & Consent", icon: <UserCheck className="h-4 w-4 text-emerald-600" aria-hidden /> },
  { id: "sharing", label: "Sharing", icon: <Share2 className="h-4 w-4 text-violet-600" aria-hidden /> },
];

// The Pickup & Consent tab shows only while PICKUP_CONSENT_ENABLED is on.
const TABS = ALL_TABS.filter((tab) => PICKUP_CONSENT_ENABLED || tab.id !== "pickup");

/**
 * `?tab=sharing` opens that tab (the dashboard's "Share" shortcuts). Only tabs
 * everyone sees; anything else opens Basic Info, as before.
 */
function tabFromSearch(search: string): TabId | null {
  const wanted = new URLSearchParams(search).get("tab");
  return TABS.find((tab) => tab.id === wanted)?.id ?? null;
}

const INTAKE_TAB: TabItem<TabId> = {
  id: "intake",
  label: "Intake",
  icon: <ClipboardList className="h-4 w-4 text-amber-600" aria-hidden />,
};

export function ChildDetail() {
  const [, params] = useRoute("/child/:id");
  const childId = params?.id;
  const [, setLocation] = useLocation();
  const { data: child, isLoading, error } = useChild(childId);
  const [isEditing, setIsEditing] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const search = useSearch();
  const [activeTab, setActiveTab] = useState<TabId>(() => tabFromSearch(search) ?? "basic");
  const deleteMutation = useDeleteChild();
  const { publish } = useToast();
  const { data: user } = useCurrentUser();
  const { data: orgs } = useMyOrgs();
  const inOrg = Boolean(user?.currentOrganizationId);
  const currentOrg = orgs?.find((o) => o.id === user?.currentOrganizationId) ?? null;
  const noun = currentOrg ? ORG_SUBJECT_NOUN[currentOrg.kind] : "child";
  const isOrgAdmin = inOrg && user?.role === "org_admin";
  const canDelete = !inOrg || isOrgAdmin;
  // Sharing is available in both personal and org context (staff may re-share an
  // org patient). Org admins additionally get the Intake tab.
  const visibleTabs = isOrgAdmin ? [...TABS, INTAKE_TAB] : TABS;

  if (!childId) return <Centered>Missing child id</Centered>;
  if (isLoading) return <PageLoader label="Loading profile…" />;

  if (error) {
    return (
      <Centered>
        <p className="text-red-600">
          {error.code === "NOT_FOUND" ? "Child not found." : error.message}
        </p>
        <Link
          href="/dashboard"
          className="mt-4 inline-block text-sm font-medium text-sky-700 hover:underline"
        >
          Back to dashboard
        </Link>
      </Centered>
    );
  }

  if (!child) return null;

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync(child.id);
      publish({
        variant: "success",
        title: "Child profile deleted successfully",
      });
      setLocation("/dashboard");
    } catch (err) {
      setConfirmDeleteOpen(false);
      publish({
        variant: "error",
        title: "Failed to delete child profile",
        description: err instanceof ApiError ? err.message : undefined,
      });
    }
  };

  return (
    <AppShell maxWidth="5xl">
      <PlayfulHero
        accent="sky"
        decor="compact"
        art="momList"
        back={{ href: "/dashboard", label: "Back to dashboard" }}
        title={
          <span className="inline-flex items-center gap-3 sm:gap-4">
            <ChildPhotoUploader
              childId={child.id}
              name={child.name}
              photoUrl={child.photoUrl ?? null}
            />
            {child.name}
          </span>
        }
        description={
          <>
            {child.age !== null ? `Age ${child.age}` : "Age not set"}
            {child.grade ? ` · ${child.grade}` : ""}
          </>
        }
        actions={
          // One row on a 390px phone: compact padding there, "Edit Profile"
          // reads "Edit", and Delete shrinks to its icon (with a spoken label).
          <>
            <Button asChild variant="secondary" className={HERO_ACTION}>
              <Link href={`/child/${child.id}/eif`}>
                <Printer className="h-4 w-4" aria-hidden />
                Emergency Form
              </Link>
            </Button>
            <Button
              variant="secondary"
              className={HERO_ACTION}
              // Same spoken name at every width (a phone shows just "Edit").
              aria-label={isEditing ? undefined : "Edit Profile"}
              onClick={() => {
                setIsEditing((v) => !v);
                setActiveTab("basic");
              }}
            >
              <Pencil className="h-4 w-4" aria-hidden />
              {isEditing ? (
                "Cancel"
              ) : (
                // One text node for the flex gap: "Edit" on a phone, "Edit Profile" above.
                <span>
                  Edit<span className="hidden sm:inline"> Profile</span>
                </span>
              )}
            </Button>
            {canDelete && (
              <Button
                variant="destructiveOutline"
                className={HERO_ACTION}
                aria-label="Delete Profile"
                onClick={() => setConfirmDeleteOpen(true)}
              >
                <Trash2 className="h-4 w-4" aria-hidden />
                <span className="hidden sm:inline">Delete Profile</span>
              </Button>
            )}
          </>
        }
      />

      {/* SIM-SEAM: child-specific season card */}
      <FeatureSlot name="childDetail.afterHero" childId={child.id} className="mt-6" />
      <TabNav tabs={visibleTabs} activeTab={activeTab} onTabChange={setActiveTab} />

      <TabPanel id="basic" activeTab={activeTab}>
        {isEditing ? (
          <EditForm child={child} onDone={() => setIsEditing(false)} />
        ) : (
          <BasicSections child={child} />
        )}
      </TabPanel>

      <TabPanel id="medical" activeTab={activeTab}>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:items-start">
          <AllergyList childId={child.id} />
          <MedicationList childId={child.id} />
          <ImmunizationsList childId={child.id} />
          <MedicalHistorySection childId={child.id} />
        </div>
      </TabPanel>

      <TabPanel id="routines" activeTab={activeTab}>
        <RoutineList childId={child.id} />
      </TabPanel>

      <TabPanel id="feeding" activeTab={activeTab}>
        <FeedingSection childId={child.id} />
      </TabPanel>

      <TabPanel id="care" activeTab={activeTab}>
        <CarePreferencesSection childId={child.id} />
      </TabPanel>

      {PICKUP_CONSENT_ENABLED && (
        <TabPanel id="pickup" activeTab={activeTab}>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:items-start">
            <AuthorizedPickupsList childId={child.id} />
            <CareAuthorizationSection childId={child.id} />
          </div>
        </TabPanel>
      )}

      <TabPanel id="sharing" activeTab={activeTab}>
        <SharingSection childId={child.id} />
      </TabPanel>

      {isOrgAdmin && (
        <TabPanel id="intake" activeTab={activeTab}>
          <IntakeSection childId={child.id} />
        </TabPanel>
      )}

      <ConfirmDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        title={`Delete ${noun} profile`}
        description={`Are you sure you want to delete ${child.name}'s ${noun === "child" ? "profile" : `${noun} record`}? This cannot be undone.`}
        confirmLabel="Delete Profile"
        destructive
        isPending={deleteMutation.isPending}
        onConfirm={() => {
          void handleDelete();
        }}
      />
    </AppShell>
  );
}

function BasicSections({
  child,
}: {
  child: NonNullable<ReturnType<typeof useChild>["data"]>;
}) {
  return (
    // Two columns of sections on a wide screen; one on a phone or tablet.
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:items-start">
      {child.hasPhoto && (
        <div className="lg:col-span-2">
          <StoredPhotoNotice childId={child.id} hasPhoto={child.hasPhoto} />
        </div>
      )}
      <InfoSection icon={User2} title="Basic Info" accent={SECTION_ACCENT.basic}>
        <InfoRow label={NICKNAME_DISPLAY_LABEL} value={child.name} />
        {/* Only when set: nickname first, the legal name is an opt-in extra. */}
        {child.legalName && (
          <InfoRow label={LEGAL_NAME_DISPLAY_LABEL} value={child.legalName} />
        )}
        <InfoRow label="Age" value={child.age} />
        <InfoRow label="Additional Notes" value={child.additionalNotes} />
      </InfoSection>

      {child.isEnrolled && (
        <InfoSection icon={GraduationCap} title="School Information" accent={SECTION_ACCENT.school}>
          <InfoRow label="Grade" value={child.grade} />
          <InfoRow label="School Name" value={child.schoolName} />
          <InfoRow label="School Phone" value={child.schoolPhone} />
          <InfoRow label="Teacher Name" value={child.teacherName} />
          <InfoRow label="School Nurse Phone" value={child.schoolNursePhone} />
        </InfoSection>
      )}

      <InfoSection icon={Phone} title="Emergency Contact" accent={SECTION_ACCENT.emergency}>
        <InfoRow label="Contact Name" value={child.emergencyContactName} />
        <InfoRow label="Phone Number" value={child.emergencyContactPhone} />
        <InfoRow label="Relationship" value={child.emergencyContactRelationship} />
      </InfoSection>

      <InfoSection icon={PhoneCall} title="Secondary Emergency Contact" accent={SECTION_ACCENT.emergency}>
        <InfoRow label="Contact Name" value={child.secondaryEmergencyContactName} />
        <InfoRow label="Phone Number" value={child.secondaryEmergencyContactPhone} />
        <InfoRow
          label="Relationship"
          value={child.secondaryEmergencyContactRelationship}
        />
      </InfoSection>
    </div>
  );
}

function EditForm({
  child,
  onDone,
}: {
  child: NonNullable<ReturnType<typeof useChild>["data"]>;
  onDone: () => void;
}) {
  const updateMutation = useUpdateChild(child.id);
  const { publish } = useToast();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<UpdateChildInput>({
    resolver: zodResolver(updateChildSchema),
    defaultValues: {
      name: child.name,
      legalName: child.legalName,
      dateOfBirth: child.dateOfBirth,
      grade: child.grade,
      additionalNotes: child.additionalNotes,
      isEnrolled: child.isEnrolled,
      schoolName: child.schoolName,
      schoolPhone: child.schoolPhone,
      teacherName: child.teacherName,
      schoolNursePhone: child.schoolNursePhone,
      emergencyContactName: child.emergencyContactName,
      emergencyContactPhone: child.emergencyContactPhone,
      emergencyContactRelationship: child.emergencyContactRelationship,
      secondaryEmergencyContactName: child.secondaryEmergencyContactName,
      secondaryEmergencyContactPhone: child.secondaryEmergencyContactPhone,
      secondaryEmergencyContactRelationship: child.secondaryEmergencyContactRelationship,
    },
  });

  const onSubmit = async (data: UpdateChildInput) => {
    try {
      await updateMutation.mutateAsync(data);
      publish({
        variant: "success",
        title: "Child profile updated successfully",
      });
      onDone();
    } catch (err) {
      publish({
        variant: "error",
        title: "Failed to update child profile",
        description: err instanceof ApiError ? err.message : undefined,
      });
    }
  };

  return (
    <Card className="max-w-3xl p-6 sm:p-8">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <Field
          label={NICKNAME_LABEL}
          htmlFor="edit-name"
          error={errors.name?.message}
          hint={NICKNAME_HINT}
        >
          <input id="edit-name" type="text" {...register("name")} className={INPUT_CLASS} />
        </Field>
        <LegalNameField
          id="edit-legal-name"
          registration={register("legalName")}
          error={errors.legalName?.message}
          hasValue={Boolean(child.legalName)}
        />
        <Field label="Date of Birth" htmlFor="edit-dob" error={errors.dateOfBirth?.message}>
          <input
            id="edit-dob"
            type="date"
            {...register("dateOfBirth", {
              setValueAs: (v) => (v === "" ? null : v),
            })}
            className={INPUT_CLASS}
          />
        </Field>
        <Field label="Additional Notes" htmlFor="edit-notes" error={errors.additionalNotes?.message}>
          <textarea id="edit-notes" rows={3} {...register("additionalNotes")} className={INPUT_CLASS} />
        </Field>
        <label className="flex cursor-pointer items-start gap-3 rounded-2xl bg-violet-50/80 p-4 ring-1 ring-violet-200">
          <input
            type="checkbox"
            {...register("isEnrolled")}
            className="mt-0.5 h-4 w-4 rounded border-neutral-300 accent-violet-600"
          />
          <span className="block text-sm font-medium text-neutral-900">
            Currently enrolled in school
          </span>
        </label>
        {watch("isEnrolled") && (
          <>
            <Field label="Grade" htmlFor="edit-grade" error={errors.grade?.message}>
              <input id="edit-grade" type="text" {...register("grade")} className={INPUT_CLASS} />
            </Field>
            <Field label="School Name" htmlFor="edit-school-name" error={errors.schoolName?.message}>
              <input id="edit-school-name" type="text" {...register("schoolName")} className={INPUT_CLASS} />
            </Field>
            <Field label="School Phone" htmlFor="edit-school-phone" error={errors.schoolPhone?.message}>
              <input id="edit-school-phone" type="tel" {...register("schoolPhone")} className={INPUT_CLASS} />
            </Field>
            <Field label="Teacher Name" htmlFor="edit-teacher-name" error={errors.teacherName?.message}>
              <input id="edit-teacher-name" type="text" {...register("teacherName")} className={INPUT_CLASS} />
            </Field>
            <Field
              label="School Nurse Phone"
              htmlFor="edit-school-nurse-phone"
              error={errors.schoolNursePhone?.message}
            >
              <input id="edit-school-nurse-phone" type="tel" {...register("schoolNursePhone")} className={INPUT_CLASS} />
            </Field>
          </>
        )}
        <Field
          label="Emergency Contact Name"
          htmlFor="edit-ec-name"
          error={errors.emergencyContactName?.message}
        >
          <input
            id="edit-ec-name"
            type="text"
            {...register("emergencyContactName")}
            className={INPUT_CLASS}
          />
        </Field>
        <Field
          label="Emergency Contact Phone"
          htmlFor="edit-ec-phone"
          error={errors.emergencyContactPhone?.message}
        >
          <input
            id="edit-ec-phone"
            type="tel"
            {...register("emergencyContactPhone")}
            className={INPUT_CLASS}
          />
        </Field>
        <Field
          label="Emergency Contact Relationship"
          htmlFor="edit-ec-relationship"
          error={errors.emergencyContactRelationship?.message}
        >
          <input
            id="edit-ec-relationship"
            type="text"
            {...register("emergencyContactRelationship")}
            className={INPUT_CLASS}
          />
        </Field>
        <Field
          label="Secondary Emergency Contact Name"
          htmlFor="edit-ec2-name"
          error={errors.secondaryEmergencyContactName?.message}
        >
          <input
            id="edit-ec2-name"
            type="text"
            {...register("secondaryEmergencyContactName")}
            className={INPUT_CLASS}
          />
        </Field>
        <Field
          label="Secondary Emergency Contact Phone"
          htmlFor="edit-ec2-phone"
          error={errors.secondaryEmergencyContactPhone?.message}
        >
          <input
            id="edit-ec2-phone"
            type="tel"
            {...register("secondaryEmergencyContactPhone")}
            className={INPUT_CLASS}
          />
        </Field>
        <Field
          label="Secondary Emergency Contact Relationship"
          htmlFor="edit-ec2-relationship"
          error={errors.secondaryEmergencyContactRelationship?.message}
        >
          <input
            id="edit-ec2-relationship"
            type="text"
            {...register("secondaryEmergencyContactRelationship")}
            className={INPUT_CLASS}
          />
        </Field>

        <div className="flex items-center justify-end gap-3">
          <Button variant="ghost" onClick={onDone}>
            Cancel
          </Button>
          <Button type="submit" isLoading={isSubmitting || updateMutation.isPending}>
            {updateMutation.isPending ? "Saving…" : "Save Changes"}
          </Button>
        </div>
      </form>
    </Card>
  );
}

function Centered({ children }: { children: ReactNode }) {
  return (
    <main className="flex min-h-screen items-center justify-center bg-gradient-to-b from-sky-50 via-white to-rose-50/60 px-6 text-center text-neutral-600">
      <div>{children}</div>
    </main>
  );
}

function InfoSection({
  icon,
  title,
  accent,
  children,
}: {
  icon: LucideIcon;
  title: string;
  accent: AccentName;
  children: ReactNode;
}) {
  return (
    <SectionCard icon={icon} title={title} accent={accent}>
      {/* Label and value side by side from sm, the label column narrow so they read as a pair. */}
      <dl className="grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-[minmax(0,11rem)_minmax(0,1fr)] sm:gap-y-3">
        {children}
      </dl>
    </SectionCard>
  );
}

function InfoRow({ label, value }: { label: string; value: string | number | null }) {
  return (
    <>
      <dt className="text-sm font-medium text-neutral-600">{label}</dt>
      <dd className="mb-2 break-words text-sm text-neutral-900 sm:mb-0">
        {value === null || value === "" ? (
          <span className="italic text-neutral-600">Not set</span>
        ) : (
          value
        )}
      </dd>
    </>
  );
}
